// EMS V2 design tokens, formatters and the polarity map (Prozo product screen standard).
// Every EMS V2 chart, tile and card takes its colours, type sizes and number formats from here;
// no component defines its own.

import { keyframes } from "@emotion/react";

export const COLORS = {
    purple: "#663F95",       // single series
    purpleSoft: "#F1ECF7",
    orange: "#EA6425",       // the one focus mark
    orangeText: "#C24E14",   // orange text on white
    orangeSoft: "#FDF0E9",
    green: "#1BAF7A",
    blue: "#2A78D6",
    pink: "#E87BA4",
    context: "#B0B0B0",      // everything that isn't the focus
    ink: "#1F2937",
    ink2: "#4B5563",
    ink3: "#6B7280",
    grid: "#E5E7EB",
    surface: "#FFFFFF",
    ground: "#F7F6FA",
    favourable: "#2A78D6",
    unfavourable: "#D03B3B",
    neutral: "#6B7280",
};

// Series order for multi-series charts; there is no sixth colour.
export const SERIES = [COLORS.purple, COLORS.orange, COLORS.green, COLORS.blue, COLORS.pink];

// Heatmap shades, light to dark purple, with the text colour that keeps 4.5:1 on each.
export const HEAT = [
    { bg: "#F4F0F8", fg: "#4B2E70" },
    { bg: "#E2D7EE", fg: "#4B2E70" },
    { bg: "#C3AEDB", fg: "#2E1B47" },
    { bg: "#8F6BB8", fg: "#FFFFFF" },
    { bg: "#663F95", fg: "#FFFFFF" },
];

export const TYPE = {
    pageTitle: { fontSize: 20, fontWeight: 600 },
    cardTitle: { fontSize: 16, fontWeight: 600 },
    kpi: { fontSize: 32, fontWeight: 600, lineHeight: 1.1 },
    body: { fontSize: 14 },
    small: { fontSize: 12 },   // nothing smaller than this
};

export const SHADOW = "0 1px 2px rgba(31,41,55,.04), 0 6px 20px rgba(76,46,112,.06)";
export const SHADOW_HOVER = "0 2px 4px rgba(31,41,55,.05), 0 12px 32px rgba(76,46,112,.12)";

export const CARD_SX = {
    bgcolor: COLORS.surface,
    border: `1px solid ${COLORS.grid}`,
    borderRadius: "12px",
    boxShadow: SHADOW,
    p: 2,
};

// One colour per headline number: a solid for marks and text, a gradient for its icon tile and
// the thin bar along the top of its card.
export const ACCENTS = {
    purple: { solid: "#663F95", soft: "#F1ECF7", grad: "linear-gradient(135deg, #8A5CC2 0%, #5A3585 100%)" },
    red: { solid: "#D03B3B", soft: "#FDECEC", grad: "linear-gradient(135deg, #F06A6A 0%, #C22F2F 100%)" },
    green: { solid: "#14936A", soft: "#E7F7F0", grad: "linear-gradient(135deg, #34C98F 0%, #128A62 100%)" },
    blue: { solid: "#2A78D6", soft: "#EAF2FC", grad: "linear-gradient(135deg, #5A9EF0 0%, #2266BD 100%)" },
    orange: { solid: "#C24E14", soft: "#FDF0E9", grad: "linear-gradient(135deg, #F58A4F 0%, #D9541A 100%)" },
};

// The band at the top of the Analytics page.
export const HERO_BG = "radial-gradient(circle at 88% 12%, rgba(255,255,255,.16) 0, rgba(255,255,255,0) 32%),"
    + " radial-gradient(circle at 70% 120%, rgba(234,100,37,.28) 0, rgba(234,100,37,0) 38%),"
    + " linear-gradient(120deg, #3E2560 0%, #663F95 55%, #8A5CC2 100%)";

export const GAP = 2; // 16px

export const MOTION_MS = 300;
export const reducedMotion = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; }
};

const riseKf = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }`;
/** Fade-and-rise on first paint, staggered by order; off under reduced motion. */
export const rise = (order = 0) => ({
    animation: `${riseKf} 420ms cubic-bezier(.2,.7,.2,1) ${Math.min(order, 8) * 60}ms both`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});
/** Lift on hover for clickable cards. */
export const LIFT = {
    transition: "box-shadow 200ms, transform 200ms, border-color 200ms",
    "&:hover": { boxShadow: SHADOW_HOVER, transform: "translateY(-2px)" },
    "@media (prefers-reduced-motion: reduce)": { "&:hover": { transform: "none" } },
};

// ── Polarity: which direction is good. A rise in backlog, TAT misses, time or reopens is bad. ──
export const POLARITY = {
    open: "lower",
    beyondTat: "lower",
    withinTatPct: "higher",
    resolutionTime: "lower",
    reopenRate: "lower",
    created: "neutral",
};

/** Colour and word for a change, decided by polarity (not by the sign). */
export const deltaTone = (polarity, delta) => {
    if (delta == null || delta === 0 || polarity === "neutral") return { color: COLORS.neutral, word: delta === 0 ? "No change" : "" };
    const good = (polarity === "higher" && delta > 0) || (polarity === "lower" && delta < 0);
    return { color: good ? COLORS.favourable : COLORS.unfavourable, word: good ? "Better" : "Worse" };
};

// ── Formatters (Indian conventions) ──
const IN = "en-IN";
export const fmtCount = (n) => (n == null || Number.isNaN(n) ? "No data" : Math.round(n).toLocaleString(IN));
export const fmtPct = (p, digits = 1) => (p == null || Number.isNaN(p) ? "No data" : `${p.toFixed(digits)}%`);
export const fmtPp = (pp) => (pp == null || Number.isNaN(pp) ? "" : `${pp > 0 ? "+" : ""}${pp.toFixed(1)} pp`);
export const fmtSigned = (n) => (n == null ? "" : `${n > 0 ? "+" : ""}${Math.round(n).toLocaleString(IN)}`);
export const fmtRupees = (n) => {
    if (n == null) return "No data";
    if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`;
    if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
    return `₹${Math.round(n).toLocaleString(IN)}`;
};
/** Minutes to "2d 4h", "3h 20m", "45m". */
export const fmtDuration = (m) => {
    if (m == null || Number.isNaN(m)) return "No data";
    const t = Math.round(m);
    if (t < 1) return "Under 1m";
    const d = Math.floor(t / 1440), h = Math.floor((t % 1440) / 60), min = t % 60;
    if (d > 0) return h ? `${d}d ${h}h` : `${d}d`;
    if (h > 0) return min ? `${h}h ${min}m` : `${h}h`;
    return `${min}m`;
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-10-13" or a Date to "13 Oct" (or "13 Oct 2026" with year). */
export const fmtDay = (d, withYear = false) => {
    if (!d) return "";
    const date = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00+05:30`) : new Date(d);
    const ist = new Date(date.getTime() + (330 + date.getTimezoneOffset()) * 60000);
    return `${String(ist.getDate()).padStart(2, "0")} ${MONTHS[ist.getMonth()]}${withYear ? ` ${ist.getFullYear()}` : ""}`;
};
export const pct = (part, whole) => (whole ? (part * 100) / whole : null);

/** "Updated 3 min ago" from a fetch time. */
export const freshness = (at, now = Date.now()) => {
    if (!at) return "";
    const s = Math.max(0, Math.round((now - at) / 1000));
    if (s < 60) return "Updated just now";
    const m = Math.round(s / 60);
    if (m < 60) return `Updated ${m} min ago`;
    const h = Math.round(m / 60);
    return `Updated ${h} h ago`;
};
